import { Signer, providers } from 'ethers'
import { toHex } from 'viem'
import type { WalletClient } from 'viem'

// Custom Ethers v5 Signer that wraps a Turnkey viem wallet client

export class TurnkeyEthersSigner extends Signer {
	private walletClient: WalletClient
	private _address: `0x${string}`
	public provider: providers.Provider | undefined

	constructor(walletClient: WalletClient, provider?: providers.Provider) {
		super()
		this.walletClient = walletClient

		if (!walletClient.account) {
			throw new Error('WalletClient must have an account')
		}

		this._address = walletClient.account.address
		this.provider = provider

		this.getAddress = this.getAddress.bind(this)
		this.signMessage = this.signMessage.bind(this)
		this.signTransaction = this.signTransaction.bind(this)
	}

	async getAddress(): Promise<string> {
		return this._address
	}

	async signMessage(message: string | Uint8Array): Promise<string> {
		if (!this.walletClient.account) {
			throw new Error('No account available')
		}

		const formattedMessage = typeof message === 'string' ? message : toHex(message)

		const signature = await this.walletClient.signMessage({
			account: this.walletClient.account,
			message: formattedMessage,
		})

		return signature
	}

	async _signTypedData(
		domain: unknown,
		types: Record<string, unknown>,
		value: unknown
	): Promise<string> {
		if (!this.walletClient.account) {
			throw new Error('No account available')
		}

		// EIP712Domain must be excluded from types but extracted to avoid passing to signTypedData
		// biome-ignore lint/style/useNamingConvention: Intentionally unused with underscore
		const { EIP712Domain: _, ...typesWithoutDomain } = types
		const signature = await this.walletClient.signTypedData({
			account: this.walletClient.account,
			// biome-ignore lint/suspicious/noExplicitAny: EIP712 domain requires flexible typing
			domain: domain as any,
			// biome-ignore lint/suspicious/noExplicitAny: EIP712 types require flexible typing
			types: typesWithoutDomain as any,
			primaryType: Object.keys(typesWithoutDomain)[0],
			// biome-ignore lint/suspicious/noExplicitAny: EIP712 message requires flexible typing
			message: value as any,
		})
		return signature
	}

	async signTransaction(transaction: providers.TransactionRequest): Promise<string> {
		if (!this.walletClient.account) {
			throw new Error('No account available')
		}

		const viemTx = {
			to: transaction.to as `0x${string}` | undefined,
			value: transaction.value ? BigInt(transaction.value.toString()) : undefined,
			data: transaction.data as `0x${string}` | undefined,
			gas: transaction.gasLimit ? BigInt(transaction.gasLimit.toString()) : undefined,
			gasPrice: transaction.gasPrice ? BigInt(transaction.gasPrice.toString()) : undefined,
			nonce: transaction.nonce !== undefined ? Number(transaction.nonce) : undefined,
			chainId: transaction.chainId !== undefined ? Number(transaction.chainId) : undefined,
		}

		// biome-ignore lint/suspicious/noExplicitAny: Transaction object requires flexible typing for viem compatibility
		const signedTx = await this.walletClient.signTransaction(viemTx as any)
		return signedTx
	}

	async sendTransaction(
		transaction: providers.TransactionRequest
	): Promise<providers.TransactionResponse> {
		if (!this.provider) {
			throw new Error('Provider not initialized')
		}
		const signedTx = await this.signTransaction(transaction)
		return this.provider.sendTransaction(signedTx)
	}

	connect(provider: providers.Provider): TurnkeyEthersSigner {
		if (!(provider instanceof providers.JsonRpcProvider)) {
			throw new Error('Provider must be a JsonRpcProvider')
		}
		return new TurnkeyEthersSigner(this.walletClient, provider)
	}
}
